import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { useAppSelector } from '../../hooks/useAppSelector';
import { 
    fetchMenuItems, 
    addMenuItem, 
    updateMenuItem, 
    deleteMenuItem, 
    toggleMenuItemAvailability, 
    toggleQuickServe,
    selectAllMenuItems,
    selectMenuLoading,
    selectMenuError
} from '../../store/slices/menuSlice';
import axios from '../../config/axiosConfig';
import { API_ENDPOINTS } from '../../config/api';
import MenuSection from './MenuSection';
import AnalyticsSection from './AnalyticsSection';
import PaymentHistory from './PaymentHistory';
import TableStatus from './Table';
import RestaurantProfile from './Profile';
import CounterOrder from './CounterOrder';
import SpecialOffersManagement from '../../components/SpecialOffersManagement';
import OrderNotifications from '../../components/OrderNotifications';
import WaiterRequestModal from '../../components/WaiterRequestModal';
import InventoryDashboard from '../../components/inventory/InventoryDashboard';
import StaffDashboard from '../../components/staff/StaffDashboard';
import OrderFlow from '../../components/restaurant/OrderFlow';
import DineInSection from './DineInSection';
import Layout from '../../components/Layout';
import AlertMessage from '../../components/AlertMessage';
import DailyAiSalesSummary from '../../components/DailyAiSalesSummary';

const ALL_DASHBOARD_TABS = [
    { id: 'ai-report', label: 'AI Daily Report', icon: '✨', shortLabel: 'AI' },
    { id: 'menu', label: 'Manage Menu', icon: '🍽️', shortLabel: 'Menu' },
    { id: 'tables', label: 'Table Management', icon: '🪑', shortLabel: 'Tables' },
    { id: 'dinein', label: 'Dine-in Reservations', icon: '📅', shortLabel: 'Dine-in' },
    { id: 'orderflow', label: 'Order Flow', icon: '🔄', shortLabel: 'Orders' },
    { id: 'counter', label: 'Counter Orders', icon: '🛒', shortLabel: 'Counter' },
    { id: 'inventory', label: 'Inventory & Recipes', icon: '📦', shortLabel: 'Inventory' },
    { id: 'staff', label: 'Staff Management', icon: '👥', shortLabel: 'Staff' },
    { id: 'offers', label: 'Special Offers', icon: '🎁', shortLabel: 'Offers' },
    { id: 'analytics', label: 'Food Analytics', icon: '📊', shortLabel: 'Analytics' },
    { id: 'payments', label: 'Payments', icon: '💳', shortLabel: 'Payments' },
    { id: 'profile', label: 'Restaurant Profile', icon: '🏪', shortLabel: 'Profile' },
];

const RestaurantDashboard = () => {
    const dispatch = useAppDispatch();
    const { tab: tabParam } = useParams();
    const navigate = useNavigate();
    const { user, restaurant } = useAppSelector(state => state.auth);
    const restaurantId = restaurant?._id || user?.id;
    
    const menuItems = useAppSelector(selectAllMenuItems);
    const menuLoading = useAppSelector(selectMenuLoading);
    const menuError = useAppSelector(selectMenuError);
    
    const [pendingRequestsCount, setPendingRequestsCount] = useState(0);
    const [showWaiterRequests, setShowWaiterRequests] = useState(false);

    const dashboardTabs = restaurant?.dashboardTabs;
    const visibleTabs = useMemo(
        () => ALL_DASHBOARD_TABS.filter((tab) => dashboardTabs == null || dashboardTabs[tab.id] !== false),
        [dashboardTabs]
    );
    const firstVisibleTabId = visibleTabs[0]?.id ?? null;
    const tabFromUrlIsValid = Boolean(tabParam && visibleTabs.some((t) => t.id === tabParam));
    const activeTab = tabFromUrlIsValid ? tabParam : (firstVisibleTabId || 'menu');

    useEffect(() => {
        if (!firstVisibleTabId) return;
        if (!tabParam || !visibleTabs.some((t) => t.id === tabParam)) {
            navigate(`/restaurant/${firstVisibleTabId}`, { replace: true });
        }
    }, [tabParam, firstVisibleTabId, navigate, visibleTabs]);

    useEffect(() => {
        if (restaurantId) {
            dispatch(fetchMenuItems(restaurantId));
        }
    }, [restaurantId, dispatch]);

    // Keep waiter request badge in sync with "active in last 24h" rules
    useEffect(() => {
        if (!restaurantId) return;

        const fetchWaiterRequestCount = async () => {
            try {
                const response = await axios.get(API_ENDPOINTS.WAITER_REQUESTS_BY_RESTAURANT(restaurantId));
                const requests = response.data || [];
                const now = Date.now();
                const oneDayMs = 24 * 60 * 60 * 1000;

                const activeRecentCount = requests.filter((req) => {
                    if (!req) return false;
                    if (req.status === 'done') return false;
                    if (!req.createdAt) return true;
                    const createdTime = new Date(req.createdAt).getTime();
                    return now - createdTime < oneDayMs;
                }).length;

                setPendingRequestsCount(activeRecentCount);
            } catch (error) {
                console.error('Failed to fetch waiter request count:', error);
            }
        };

        fetchWaiterRequestCount();
        const interval = setInterval(fetchWaiterRequestCount, 10000);
        return () => clearInterval(interval);
    }, [restaurantId]);

    // Add menu item
    const handleAddMenuItem = async (newItem) => {
        try {
            const itemWithRestaurantId = { ...newItem, restaurantId };
            const savedItem = await dispatch(addMenuItem(itemWithRestaurantId)).unwrap();
            // Refresh menu items to ensure the image is displayed
            if (restaurantId) {
                dispatch(fetchMenuItems(restaurantId));
            }
            return savedItem;
        } catch (err) {
            console.error('Failed to add menu item:', err);
            throw err;
        }
    };

    // Update menu item
    const handleUpdateMenuItem = async (id, updatedItem) => {
        try {
            const updatedItemResult = await dispatch(updateMenuItem({ id, updates: updatedItem })).unwrap();
            // Refresh menu items to ensure the image is displayed
            if (restaurantId) {
                dispatch(fetchMenuItems(restaurantId));
            }
            return updatedItemResult;
        } catch (err) {
            console.error('Failed to update menu item:', err);
            throw err;
        }
    };

    // Delete menu item
    const handleDeleteMenuItem = async (id) => {
        try {
            await dispatch(deleteMenuItem(id)).unwrap();
        } catch (err) {
            console.error('Failed to delete menu item:', err);
        }
    };

    // Toggle the availability of a menu item
    const handleToggleAvailability = async (id) => {
        try {
            await dispatch(toggleMenuItemAvailability(id)).unwrap();
        } catch (err) {
            console.error('Failed to toggle availability:', err);
        }
    };

    const handleToggleQuickServe = async (id) => {
        try {
            await dispatch(toggleQuickServe(id)).unwrap();
        } catch (err) {
            console.error('Failed to toggle quick serve:', err);
        }
    };

    const renderTabContent = (tabId = activeTab) => {
        switch (tabId) {
            case 'ai-report':
                return <DailyAiSalesSummary restaurantId={restaurantId} />;
            case 'menu':
                return (
                    <MenuSection
                        menuItems={menuItems}
                        allMenuItems={menuItems}
                        addMenuItem={handleAddMenuItem}
                        updateMenuItem={handleUpdateMenuItem}
                        deleteMenuItem={handleDeleteMenuItem}
                        toggleAvailability={handleToggleAvailability}
                        handleToggelQuickServe={handleToggleQuickServe}
                    />
                );
            case 'tables':
                return <TableStatus />;
            case 'dinein':
                return <DineInSection restaurantId={restaurantId} />;
            case 'orderflow':
                return <OrderFlow />;
            case 'counter':
                return <CounterOrder />;
            case 'offers':
                return <SpecialOffersManagement />;
            case 'analytics':
                return <AnalyticsSection menuItems={menuItems} />;
            case 'payments':
                return <PaymentHistory />;
            case 'inventory':
                return <InventoryDashboard />;
            case 'staff':
                return <StaffDashboard />;
            case 'profile':
                return <RestaurantProfile />;
            default:
                return null;
        }
    };

    return (
        <Layout restaurantDetails={restaurant}>
            <div className="w-full lg:mt-5 px-0 sm:px-4 lg:px-6">
                <div className="flex flex-row items-center justify-between gap-2 mt-3 mb-4 sm:mb-6 min-w-0 px-3 sm:px-0">
                    <h1 className="text-xl sm:text-3xl font-bold truncate min-w-0 flex-shrink">Restaurant Dashboard</h1>
                    <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                            onClick={() => {
                                setShowWaiterRequests(true);
                            }}
                            className="relative bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 text-white px-2 sm:px-3 py-1.5 rounded-lg font-semibold transition-colors flex items-center gap-1"
                        >
                            <span className="text-sm">👋</span>
                            <span className="hidden sm:inline text-xs">Waiter Requests</span>
                            {pendingRequestsCount > 0 && (
                                <span className="absolute -top-0.5 -right-0.5 sm:-top-1 sm:-right-1 bg-yellow-400 text-black text-[9px] sm:text-[10px] font-bold rounded-full min-w-[1rem] h-4 sm:w-5 sm:h-5 flex items-center justify-center">
                                    {pendingRequestsCount}
                                </span>
                            )}
                        </button>
                        <OrderNotifications />
                    </div>
                </div>

                {/* Tab Navigation */}
                <div className="mb-0 sm:mb-6 overflow-hidden sm:bg-white sm:rounded-xl sm:shadow-lg sm:border sm:border-slate-200/80">
                    {/* Mobile: full width tab strip */}
                    <div className="sm:border-0 sm:rounded-none sm:shadow-none sm:bg-transparent sm:border-b sm:border-slate-200">
                        <div
                            className="flex sm:flex-wrap overflow-x-auto sm:overflow-visible scroll-smooth gap-1.5 sm:gap-0 p-2 sm:p-0 bg-slate-100/90 rounded-none sm:rounded-none"
                            style={{ WebkitOverflowScrolling: 'touch' }}
                        >
                            {visibleTabs.map((tab) => (
                                <button
                                    key={tab.id}
                                    type="button"
                                    onClick={() => navigate(`/restaurant/${tab.id}`)}
                                    className={`
                                        flex-shrink-0 transition-all duration-300 min-h-[52px] sm:min-h-0
                                        flex flex-col sm:flex-row items-center justify-center sm:justify-start gap-0.5 sm:gap-2
                                        px-4 py-2.5 sm:px-5 sm:py-4 font-medium whitespace-nowrap
                                        rounded-xl sm:rounded-none border-l-0 sm:border-l-0 border-b-2 sm:border-b-2 border-transparent
                                        ${activeTab === tab.id
                                            ? 'bg-white shadow-md border-slate-200/60 text-slate-800 sm:bg-inherit sm:shadow-none sm:border-indigo-500 sm:text-indigo-600'
                                            : 'text-slate-500 active:bg-white/50 sm:text-gray-500 sm:hover:text-indigo-600 sm:border-transparent'
                                        }
                                    `}
                                >
                                    <span className="text-[22px] sm:text-lg leading-none">{tab.icon}</span>
                                    <span className="text-[11px] sm:text-base hidden sm:inline font-medium">{tab.label}</span>
                                    <span className="text-[11px] sm:hidden font-medium">{tab.shortLabel}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="px-0 py-4 sm:p-6 bg-white sm:rounded-b-xl">
                        {menuError && (
                            <div className="mb-4 sm:mb-6">
                                <AlertMessage
                                    message={menuError}
                                    type="error"
                                    onDismiss={() => {}}
                                    dismissible={false}
                                />
                            </div>
                        )}
                        
                        {menuLoading && (
                            <div className="text-center py-8">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                                <p className="mt-2 text-gray-600">Loading menu items...</p>
                            </div>
                        )}
                        
                        {!menuLoading && renderTabContent(activeTab)}
                    </div>
                </div>
            </div>
            
            <WaiterRequestModal 
                isOpen={showWaiterRequests}
                onClose={() => {
                    setShowWaiterRequests(false);
                }}
                restaurantId={restaurantId}
            />
        </Layout>
    );
};

export default RestaurantDashboard;

